/**
 * UseWithParamsEditor — shared visual editor for ::use `with` template params.
 */

import type {
    TemplateInputSchema,
    UseBinding,
} from "@magic-md/core";
import { RefOrValue, type RefOrValueProps, type ScopeVariable } from "@magic-md/plugin-sdk";
import { useT } from "@magic-md/i18n/react";
import { ChevronDown, ChevronRight, FileText } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getThemeAccentStyle } from "../theme/runtime/theme-accents";

export interface UseWithParamsEditorProps {
    useBinding: Pick<UseBinding, "id" | "with">;
    templateParams: string[];
    inputSchema?: TemplateInputSchema;
    context: RefOrValueProps["context"];
    onApplyWith: (withParams: Record<string, unknown>) => void;
    variant?: "section" | "plain";
    /** Stable identity for a concrete same-ID ::use occurrence. */
    targetKey?: string;
}

export function UseWithParamsEditor({
    useBinding,
    templateParams,
    inputSchema,
    context,
    onApplyWith,
    variant = "section",
    targetKey,
}: UseWithParamsEditorProps) {
    const t = useT("editor");
    const persistedWith = useBinding.with ?? {};
    const templateFields = useMemo(
        () => buildTemplateFields(templateParams, inputSchema),
        [templateParams, inputSchema],
    );
    const fieldNames = useMemo(
        () => templateFields.map((field) => field.name),
        [templateFields],
    );

    const [expanded, setExpanded] = useState(
        () => Object.keys(persistedWith).length > 0,
    );
    const [draftWith, setDraftWith] = useState<Record<string, unknown>>(() => {
        const obj: Record<string, unknown> = {};
        for (const p of fieldNames) {
            if (persistedWith[p] != null) obj[p] = persistedWith[p];
        }
        return obj;
    });

    const useTargetKey = targetKey ?? useBinding.id;
    const lastUseTargetKey = useRef(useTargetKey);
    useEffect(() => {
        if (useTargetKey !== lastUseTargetKey.current) {
            lastUseTargetKey.current = useTargetKey;
            const w = useBinding.with ?? {};
            const obj: Record<string, unknown> = {};
            for (const p of fieldNames) {
                if (w[p] != null) obj[p] = w[p];
            }
            setDraftWith(obj);
        }
    }, [useTargetKey, useBinding.with, fieldNames]);

    useEffect(() => {
        setDraftWith((prev) => {
            const next: Record<string, unknown> = {};
            for (const p of fieldNames) {
                next[p] = prev[p] ?? (persistedWith[p] != null ? persistedWith[p] : undefined);
            }
            return next;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fieldNames.join(",")]);

    const persistedJson = JSON.stringify(
        Object.fromEntries(
            fieldNames.map((p) => [p, persistedWith[p] ?? undefined]),
        ),
    );
    const draftJson = useMemo(() => {
        const obj: Record<string, unknown> = {};
        for (const p of fieldNames) {
            obj[p] = draftWith[p] ?? undefined;
        }
        return JSON.stringify(obj);
    }, [draftWith, fieldNames]);
    const isDirty = draftJson !== persistedJson;

    const filledCount = templateFields.filter((field) => {
        const v = draftWith[field.name];
        return hasUsableValue(v) || hasDefaultValue(field.property);
    }).length;
    const missingRequiredCount = templateFields.filter((field) =>
        isMissingRequiredField(field, draftWith)
    ).length;

    const templateInputScopeVariables = useMemo(
        () => buildTemplateInputScopeVariables(templateFields, draftWith, t),
        [templateFields, draftWith, t],
    );
    const refScopeVariables = useMemo(() => {
        const seen = new Set<string>();
        const merged: ScopeVariable[] = [];
        for (const variable of templateInputScopeVariables) {
            seen.add(variable.displayName);
            merged.push(variable);
        }
        for (const variable of context.scopeVariables ?? []) {
            if (seen.has(variable.displayName)) continue;
            merged.push(variable);
        }
        return merged;
    }, [context.scopeVariables, templateInputScopeVariables]);
    const refContext = useMemo<RefOrValueProps["context"]>(
        () => ({
            ...context,
            scopeVariables: refScopeVariables,
        }),
        [context, refScopeVariables],
    );

    const handleConfigChange = useCallback((newConfig: Record<string, unknown>) => {
        setDraftWith(newConfig);
    }, []);

    const applyWith = useCallback(() => {
        const withObj: Record<string, unknown> = {};
        for (const p of fieldNames) {
            const v = draftWith[p];
            if (hasUsableValue(v)) withObj[p] = v;
        }
        onApplyWith(withObj);
    }, [draftWith, fieldNames, onApplyWith]);

    if (templateFields.length === 0) return null;

    const fields = templateFields.map((field) => {
        const label = field.property?.title?.trim() || field.name;
        const missingRequired = isMissingRequiredField(field, draftWith);
        return (
            <div key={field.name} className="space-y-1">
                <RefOrValue
                    label={(
                        <span className="flex min-w-0 items-center gap-1">
                            <span className="truncate">{label}</span>
                            {field.property?.type ? (
                                <span className="shrink-0 rounded bg-muted/60 px-1 py-0 text-[8px] font-mono text-muted-foreground/60">
                                    {field.property.type}
                                </span>
                            ) : null}
                            {field.required ? (
                                <span
                                    className="shrink-0 text-[8px] font-medium theme-accent-text"
                                    style={getThemeAccentStyle("warning")}
                                >
                                    {t("common.required")}
                                </span>
                            ) : null}
                        </span>
                    )}
                    refField={field.name}
                    manualFields={[]}
                    config={draftWith}
                    onChange={handleConfigChange}
                    context={refContext}
                    borderless
                >
                    {renderInputControl(field, draftWith[field.name], (value) => {
                        setDraftWith((prev) => ({
                            ...prev,
                            [field.name]: value,
                        }));
                    }, t)}
                </RefOrValue>
                {missingRequired ? (
                    <div
                        className="px-0.5 text-[9px] leading-4 font-medium theme-accent-text"
                        style={getThemeAccentStyle("warning")}
                    >
                        {t("template.missingRequired")}
                    </div>
                ) : null}
                {field.property?.description ? (
                    <div className="px-0.5 text-[9px] leading-4 text-muted-foreground/60">
                        {field.property.description}
                    </div>
                ) : null}
            </div>
        );
    });

    const saveButton = isDirty ? (
        <button
            type="button"
            className="flex shrink-0 items-center gap-0.5 rounded px-1.5 py-0.5 text-[10px] font-medium theme-accent-icon-button"
            style={getThemeAccentStyle("success")}
            onClick={applyWith}
        >
            {t("common.save")}
        </button>
    ) : null;

    if (variant === "plain") {
        return (
            <div className="space-y-3">
                {fields}
                {saveButton && (
                    <div className="flex justify-end pt-1">
                        {saveButton}
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="overflow-hidden rounded-lg border border-border/30">
            <div className="flex items-center gap-1.5 bg-muted/50 px-2.5 py-1.5 transition-colors">
                <button
                    type="button"
                    className="flex min-w-0 flex-1 items-center gap-1.5"
                    onClick={() => setExpanded((v) => !v)}
                >
                    <span className="text-muted-foreground/60"><FileText size={12} /></span>
                    <span className="flex-1 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                        {t("template.params")}
                    </span>
                    {!expanded && filledCount > 0 && (
                        <span className="text-[9px] font-medium tabular-nums text-muted-foreground/50">
                            {filledCount}/{templateFields.length}
                        </span>
                    )}
                    {!expanded && missingRequiredCount > 0 && (
                        <span
                            className="text-[9px] font-medium theme-accent-text"
                            style={getThemeAccentStyle("warning")}
                        >
                            {t("template.missingRequiredCount", { count: missingRequiredCount })}
                        </span>
                    )}
                    {!expanded && missingRequiredCount === 0 && filledCount < templateFields.length && (
                        <span className="text-[9px] font-medium text-muted-foreground/50">
                            {t("template.unfilledCount", { count: templateFields.length - filledCount })}
                        </span>
                    )}
                    {expanded ? (
                        <ChevronDown size={11} className="shrink-0 text-muted-foreground/50" />
                    ) : (
                        <ChevronRight size={11} className="shrink-0 text-muted-foreground/50" />
                    )}
                </button>
                {saveButton}
            </div>

            {expanded && (
                <div className="space-y-2 border-t border-border/20 px-2.5 py-2">
                    {fields}
                </div>
            )}
        </div>
    );
}

type TemplateInputProperty = NonNullable<TemplateInputSchema["properties"]>[string];

interface TemplateInputField {
    name: string;
    property?: TemplateInputProperty;
    required: boolean;
}

function buildTemplateFields(
    params: readonly string[],
    schema: TemplateInputSchema | undefined,
): TemplateInputField[] {
    const properties = schema?.properties ?? {};
    const ordered = schema?.order?.filter((name) => properties[name]) ?? [];
    const names = [
        ...ordered,
        ...Object.keys(properties).filter((name) => !ordered.includes(name)),
        ...params.filter((name) => !properties[name]),
    ];
    const required = new Set(schema?.required ?? []);
    return [...new Set(names)].map((name) => ({
        name,
        property: properties[name],
        required: required.has(name),
    }));
}

function buildTemplateInputScopeVariables(
    fields: readonly TemplateInputField[],
    values: Record<string, unknown>,
    t: (key: string, params?: Record<string, unknown>) => string,
): ScopeVariable[] {
    return fields.map((field) => {
        const value = values[field.name];
        return {
            displayName: `input/${field.name}`,
            refTemplate: `$ref{input/${field.name}}`,
            origin: { type: "custom" as const, directiveName: "template-input" },
            description: field.property?.title || field.property?.description || t("template.inputFallback"),
            sampleValue: value,
            browsable: typeof value === "object" && value !== null,
            color: "blue" as const,
        };
    });
}

function renderInputControl(
    field: TemplateInputField,
    value: unknown,
    onChange: (value: unknown) => void,
    t: (key: string, params?: Record<string, unknown>) => string,
) {
    const prop = field.property;
    const type = prop?.type ?? "string";
    const placeholder = hasDefaultValue(prop)
        ? t("template.defaultLabel", { value: formatInputValue(prop.default) })
        : t("template.valuePlaceholder", { name: field.name });
    const baseClass = "w-full rounded border border-border bg-background px-2 text-[11px] font-mono focus:outline-none focus:ring-1 focus:ring-primary/50";

    if (prop?.enum?.length) {
        return (
            <select
                value={value == null ? "" : String(value)}
                onChange={(event) => {
                    const selected = event.target.value;
                    if (!selected) {
                        onChange(undefined);
                        return;
                    }
                    onChange(prop.enum?.find((item) => String(item) === selected) ?? selected);
                }}
                className={`${baseClass} h-7`}
            >
                <option value="">{placeholder}</option>
                {prop.enum.map((item) => (
                    <option key={String(item)} value={String(item)}>
                        {String(item)}
                    </option>
                ))}
            </select>
        );
    }

    if (type === "boolean") {
        return (
            <select
                value={value === undefined || value === null ? "" : String(Boolean(value))}
                onChange={(event) => {
                    if (!event.target.value) onChange(undefined);
                    else onChange(event.target.value === "true");
                }}
                className={`${baseClass} h-7`}
            >
                <option value="">{placeholder}</option>
                <option value="true">true</option>
                <option value="false">false</option>
            </select>
        );
    }

    if (type === "number" || type === "integer") {
        return (
            <input
                type="number"
                step={type === "integer" ? 1 : "any"}
                value={value == null ? "" : String(value)}
                onChange={(event) => {
                    const raw = event.target.value;
                    if (raw === "") {
                        onChange(undefined);
                        return;
                    }
                    const numeric = Number(raw);
                    onChange(Number.isFinite(numeric) ? numeric : raw);
                }}
                placeholder={placeholder}
                className={`${baseClass} h-7`}
            />
        );
    }

    if (type === "array" || type === "object") {
        return (
            <textarea
                value={value == null ? "" : formatInputValue(value)}
                onChange={(event) => {
                    const raw = event.target.value;
                    if (!raw.trim()) {
                        onChange(undefined);
                        return;
                    }
                    try {
                        onChange(JSON.parse(raw));
                    } catch {
                        onChange(raw);
                    }
                }}
                placeholder={placeholder}
                className={`${baseClass} min-h-14 py-1 leading-4`}
            />
        );
    }

    return (
        <input
            type="text"
            value={value != null ? String(value) : ""}
            onChange={(event) => onChange(event.target.value || undefined)}
            placeholder={placeholder}
            className={`${baseClass} h-7`}
        />
    );
}

function hasUsableValue(value: unknown): boolean {
    if (value === undefined || value === null) return false;
    if (typeof value === "string") return value.trim() !== "";
    return true;
}

function isMissingRequiredField(
    field: TemplateInputField,
    values: Record<string, unknown>,
): boolean {
    if (!field.required) return false;
    if (hasDefaultValue(field.property)) return false;
    return !hasUsableValue(values[field.name]);
}

function hasDefaultValue(prop: TemplateInputProperty | undefined): prop is TemplateInputProperty & { default: unknown } {
    return !!prop && Object.prototype.hasOwnProperty.call(prop, "default");
}

function formatInputValue(value: unknown): string {
    if (typeof value === "string") return value;
    if (value === undefined || value === null) return "";
    try {
        return JSON.stringify(value);
    } catch {
        return String(value);
    }
}
